import { api } from '@/shared/api/axios';
import type {
  ProductStockPosition,
  PurchaseRequisition,
  Rfq,
  RfqComparison,
  SupplierRating,
  SupplierScorecard,
} from '../types';
import { localToday } from '@/shared/utils/utils';

/**
 * Thin API layer for the RFQ / supplier-scorecard / stock-position endpoints.
 *
 * Plain async functions over the shared axios client rather than react-query
 * hooks, deliberately matching ProcureToPayPage.tsx — the screen these render
 * inside loads every tab with `api.get(...)` into local state, and mixing a
 * second data-fetching style into the same page would make the loading and
 * refresh behaviour inconsistent between neighbouring tabs.
 *
 * The accounting API wraps every response as { ok, data, meta }, so each helper
 * unwraps `.data.data` once here instead of at ten call sites.
 */

interface AccountingEnvelope<T> { ok: boolean; data: T; meta?: unknown }

const RFQ_BASE = '/api/accounting/rfq';
const SCORECARD_BASE = '/api/accounting/supplier-scorecard';
const P2P_BASE = '/api/accounting/p2p';

// ── RFQ ──────────────────────────────────────────────────────────────────────

export async function listRfqs(params: { status?: string } = {}): Promise<Rfq[]> {
  const res = await api.get<AccountingEnvelope<{ items: Rfq[]; total: number }>>(RFQ_BASE, { params });
  return res.data?.data?.items ?? [];
}

export async function getRfq(id: string): Promise<{ rfq: Rfq; comparison: RfqComparison }> {
  const res = await api.get<AccountingEnvelope<{ rfq: Rfq; comparison: RfqComparison }>>(`${RFQ_BASE}/${id}`);
  return res.data.data;
}

export async function createRfq(body: {
  title?: string;
  requisitionId?: string;
  dueDate?: string;
  notes?: string;
  items?: Array<Record<string, unknown>>;
  suppliers: Array<{ supplierId: string; supplierName?: string }>;
}): Promise<Rfq> {
  const res = await api.post<AccountingEnvelope<Rfq>>(RFQ_BASE, body);
  return res.data.data;
}

export async function sendRfq(id: string): Promise<Rfq> {
  const res = await api.post<AccountingEnvelope<Rfq>>(`${RFQ_BASE}/${id}/send`, {});
  return res.data.data;
}

export async function recordQuote(id: string, body: Record<string, unknown>): Promise<{ rfq: Rfq; comparison: RfqComparison }> {
  const res = await api.post<AccountingEnvelope<{ rfq: Rfq; comparison: RfqComparison }>>(`${RFQ_BASE}/${id}/quotes`, body);
  return res.data.data;
}

export async function selectSupplier(id: string, supplierId: string, selectionReason = ''): Promise<{ rfq: Rfq; comparison: RfqComparison }> {
  const res = await api.post<AccountingEnvelope<{ rfq: Rfq; comparison: RfqComparison }>>(
    `${RFQ_BASE}/${id}/select`, { supplierId, selectionReason },
  );
  return res.data.data;
}

export async function cancelRfq(id: string, reason = ''): Promise<Rfq> {
  const res = await api.post<AccountingEnvelope<Rfq>>(`${RFQ_BASE}/${id}/cancel`, { reason });
  return res.data.data;
}

/**
 * Award the RFQ and raise the resulting purchase order.
 *
 * Three calls on purpose. /award returns the PO body but deliberately does NOT
 * write the order itself: POST /p2p/pos is the one tested PO writer — it owns
 * line totals, tax, extra charges and numbering — and a second writer inside
 * the RFQ router would drift from it the first time that arithmetic changed.
 * The created order's id is then recorded back on the RFQ, which is what stops
 * the same RFQ being converted twice.
 */
export async function awardRfqAndRaisePo(id: string): Promise<{ rfq: Rfq; purchaseOrderId: string }> {
  const awarded = await api.post<AccountingEnvelope<{ rfq: Rfq; purchaseOrderDraft: Record<string, unknown> }>>(
    `${RFQ_BASE}/${id}/award`, {},
  );
  const draft = awarded.data.data.purchaseOrderDraft;

  const po = await api.post<AccountingEnvelope<{ _id: string }>>(`${P2P_BASE}/pos`, {
    ...draft,
    date: localToday(),
  });
  const purchaseOrderId = po.data.data._id;

  const linked = await api.post<AccountingEnvelope<Rfq>>(`${RFQ_BASE}/${id}/link-purchase-order`, { purchaseOrderId });
  return { rfq: linked.data.data, purchaseOrderId };
}

// ── Supplier scorecard ───────────────────────────────────────────────────────

export async function listScorecards(supplierIds?: string[]): Promise<SupplierScorecard[]> {
  const params = supplierIds?.length ? { supplierIds: supplierIds.join(',') } : {};
  const res = await api.get<AccountingEnvelope<{ items: SupplierScorecard[] }>>(`${SCORECARD_BASE}/scorecards`, { params });
  return res.data?.data?.items ?? [];
}

export async function getSupplierScorecard(supplierId: string): Promise<{ scorecard: SupplierScorecard; ratings: SupplierRating[] }> {
  const res = await api.get<AccountingEnvelope<{ scorecard: SupplierScorecard; ratings: SupplierRating[] }>>(
    `${SCORECARD_BASE}/supplier/${supplierId}`,
  );
  return res.data.data;
}

export async function rateSupplier(body: {
  supplierId: string;
  supplierName?: string;
  sourceType?: 'grn' | 'po' | 'manual';
  grnId?: string;
  poId?: string;
  scores: Record<string, number>;
  comments?: string;
}): Promise<SupplierRating> {
  const res = await api.post<AccountingEnvelope<SupplierRating>>(SCORECARD_BASE, body);
  return res.data.data;
}

// ── Stock position (Procure-to-Pay router) ───────────────────────────────────

/** Current stock, minimum stock and stock-in-transit for one product. */
export async function getProductStockPosition(productId: string): Promise<ProductStockPosition> {
  const res = await api.get<AccountingEnvelope<ProductStockPosition>>(`${P2P_BASE}/product-stock/${productId}`);
  return res.data.data;
}

/**
 * One purchase requisition, so "raise an RFQ from this PR" can show the lines
 * the suppliers will be asked to quote against before the RFQ is created.
 *
 * Uses GET /p2p/requisitions/:id, which the P2P router added for exactly this
 * ("so an RFQ can be raised from it without a list scan"), rather than reusing
 * a row out of the requisitions list — the list is paginated, so the PR being
 * referenced is not guaranteed to be on the page the caller happens to hold.
 */
export async function getRequisition(id: string): Promise<PurchaseRequisition> {
  const res = await api.get<AccountingEnvelope<PurchaseRequisition>>(`${P2P_BASE}/requisitions/${id}`);
  return res.data.data;
}

/** Requisitions to raise an RFQ from. `status` filters server-side. */
export async function listRequisitions(params: { status?: string } = {}): Promise<PurchaseRequisition[]> {
  const res = await api.get<AccountingEnvelope<{ items: PurchaseRequisition[]; total: number }>>(
    `${P2P_BASE}/requisitions`, { params },
  );
  return res.data?.data?.items ?? [];
}
